import type { AuthError } from '@supabase/supabase-js'
import type { en } from '../../i18n/en'
import { api, unwrap } from '../../lib/api'
import type { Schemas } from '../../lib/api'
import { TERMS_VERSION } from '../../lib/rules'
import { supabase } from '../../lib/supabase'
import { log } from '../../lib/telemetry'

/** Why an auth request failed, as the forms explain it. */
export type AuthFailureReason = keyof typeof en.auth.errors

/** A failed sign-in, sign-up, code check or password change. */
export class AuthFailure extends Error {
  /** @param reason - What the form tells the user. */
  constructor(readonly reason: AuthFailureReason) {
    super(reason)
    this.name = 'AuthFailure'
  }
}

/**
 * The reason of anything thrown by the functions of this module.
 * @param error - The thrown value.
 * @returns Its reason; `unknown` for anything that is not an {@link AuthFailure}.
 */
export function failureReason(error: unknown): AuthFailureReason {
  return error instanceof AuthFailure ? error.reason : 'unknown'
}

/**
 * Maps a Supabase auth error to what the form shows.
 * @param error - Error returned by supabase-js.
 * @param context - `signup` turns a database error into a taken username: the
 *   trigger that creates the profile is the only thing that can fail there.
 * @returns The failure to throw.
 */
export function toAuthFailure(error: AuthError, context?: 'signup'): AuthFailure {
  switch (error.code) {
    case 'invalid_credentials':
      return new AuthFailure('invalid_credentials')
    case 'email_not_confirmed':
      return new AuthFailure('email_not_confirmed')
    case 'weak_password':
      return new AuthFailure('weak_password')
    case 'same_password':
      return new AuthFailure('same_password')
    case 'captcha_failed':
      return new AuthFailure('captcha_failed')
    case 'otp_expired':
      return new AuthFailure('invalid_code')
    case 'over_email_send_rate_limit':
    case 'over_request_rate_limit':
      return new AuthFailure('rate_limited')
    case 'unexpected_failure':
      if (context === 'signup') return new AuthFailure('signup_failed')
  }
  if (error.status === 429) return new AuthFailure('rate_limited')
  return new AuthFailure('unknown')
}

/**
 * Signs in with email and password.
 * @param email - Email address.
 * @param password - Password.
 * @param captchaToken - Fresh Turnstile token.
 * @throws {AuthFailure} On wrong credentials, unconfirmed email or failed captcha.
 */
export async function signIn(email: string, password: string, captchaToken: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password, options: { captchaToken } })
  if (error) throw toAuthFailure(error)
}

/** What the sign-up form sends. */
export interface SignUpForm {
  /** Email address, confirmed by the code. */
  email: string
  /** Password, at least 10 characters. */
  password: string
  /** Username, checked by the database trigger. */
  username: string
}

/**
 * Creates the account and has auth email the confirmation code. The account
 * cannot sign in until {@link confirmSignUp}.
 * @param form - Email, password and username.
 * @param captchaToken - Fresh Turnstile token.
 * @throws {AuthFailure} `signup_failed` when the username was taken in the meantime.
 */
export async function signUp(form: SignUpForm, captchaToken: string): Promise<void> {
  const { error } = await supabase.auth.signUp({
    email: form.email,
    password: form.password,
    options: { captchaToken, data: { username: form.username, terms_version: TERMS_VERSION } },
  })
  if (error) throw toAuthFailure(error, 'signup')
  log('code_requested', { kind: 'signup' })
}

/**
 * Checks the sign-up code; on success the user is signed in.
 * @param email - Address the code was sent to.
 * @param code - The 6 digits.
 * @throws {AuthFailure} `invalid_code` when it is wrong or expired.
 */
export async function confirmSignUp(email: string, code: string): Promise<void> {
  const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' })
  if (error) throw toAuthFailure(error)
}

/**
 * Sends a new sign-up code.
 * @param email - Address of the unconfirmed account.
 * @param captchaToken - Fresh Turnstile token.
 * @throws {AuthFailure} When sending is refused.
 */
export async function resendSignUpCode(email: string, captchaToken: string): Promise<void> {
  const { error } = await supabase.auth.resend({ type: 'signup', email, options: { captchaToken } })
  if (error) throw toAuthFailure(error)
  log('code_requested', { kind: 'signup_resend' })
}

/**
 * Has auth email a password reset code. Answers the same whether the address
 * has an account or not.
 * @param email - Address of the account.
 * @param captchaToken - Fresh Turnstile token.
 * @throws {AuthFailure} When sending is refused.
 */
export async function requestPasswordReset(email: string, captchaToken: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, { captchaToken })
  if (error) throw toAuthFailure(error)
  log('code_requested', { kind: 'recovery' })
}

/**
 * Checks a password reset code, which signs the user in.
 * @param email - Address the code was sent to.
 * @param code - The 6 digits.
 * @throws {AuthFailure} `invalid_code` when it is wrong or expired.
 */
export async function verifyRecoveryCode(email: string, code: string): Promise<void> {
  const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'recovery' })
  if (error) throw toAuthFailure(error)
}

/**
 * Sets a new password for the signed-in user.
 * @param password - The new password.
 * @throws {AuthFailure} When it is too weak or the same as before.
 */
export async function updatePassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password })
  if (error) throw toAuthFailure(error)
}

/** Signs out on this device. */
export async function signOut(): Promise<void> {
  await supabase.auth.signOut({ scope: 'local' })
}

/** Answer of the username check. */
export type UsernameAvailability = Schemas['Availability']

/**
 * Asks the API whether a username is free.
 * @param name - Username to check.
 * @returns Whether it is available, and why not.
 */
export function checkUsername(name: string): Promise<UsernameAvailability> {
  return unwrap(api.GET('/usernames/{name}', { params: { path: { name } } }))
}
